import { Check } from '@/src/assets/icons/Check'
import { CircleWarning } from '@/src/assets/icons/CircleWarning'
import MoreHorizontal from '@/src/assets/icons/MoreHorizontal'
import { TrashIcon } from '@/src/assets/icons/trash'
import { useLogError } from '@/src/common/hooks/useLogError'
import { mesaColorNames } from '@/src/common/theme'
import { triggerSegmentAction } from '@/src/services/segment/triggerSegmentEvents'
import { useEffect, useRef, useState } from 'react'
import { TouchableOpacity } from 'react-native'
import { Paragraph, Spinner, useEvent, View } from 'tamagui'

export type DocumentToUploadStatus = 'waiting' | 'uploading' | 'uploaded' | 'error'

function DocumentToUploadIcon({
  status,
  disabled,
  onDeletePress,
}: {
  status: DocumentToUploadStatus
  disabled: boolean
  onDeletePress: () => void
}) {
  const handleDeletePress = useEvent(() => {
    void triggerSegmentAction({
      component: 'Button',
      action: 'MesaHome MortgagePayment UnselectDocument',
    })
    onDeletePress()
  })
  if (!disabled) {
    return (
      <TouchableOpacity onPress={handleDeletePress}>
        <TrashIcon svgProps={{ width: 20, height: 20 }} />
      </TouchableOpacity>
    )
  }
  switch (status) {
    case 'waiting':
      return <MoreHorizontal width={20} height={20} />
    case 'uploading':
      return (
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment -- nondeterministic type complexity */
        /* @ts-ignore super weird.. this happens here but not anywhere else */
        <Spinner
          width={20}
          height={20}
          color={mesaColorNames['Mesa Primary Scale/400 - Brand #00DFC9']}
        />
      )
    case 'uploaded':
      return <Check width={20} height={20} color={mesaColorNames['semantic/fg/success #008557']} />
    case 'error':
      return (
        <CircleWarning width={20} height={20} color={mesaColorNames['semantic/fg/error #DB340B']} />
      )
  }
}

interface DocumentToUploadProps {
  disabled?: boolean
  fileName: string
  shouldUpload: boolean
  performUpload: () => Promise<boolean>
  onUploadComplete: () => void
  onUploadError: () => void
  onDeletePress: () => void
}

export function DocumentToUpload({
  disabled,
  fileName,
  performUpload,
  onUploadComplete,
  onUploadError,
  onDeletePress,
  shouldUpload,
}: DocumentToUploadProps) {
  const logError = useLogError()
  const hasUploadStarted = useRef(false)
  const [status, setStatus] = useState<DocumentToUploadStatus>('waiting')

  const uploadWithStateUpdates = useEvent(async () => {
    setStatus('uploading')

    try {
      const success = await performUpload()
      if (success) {
        setStatus('uploaded')
        onUploadComplete()
      } else {
        setStatus('error')
        onUploadError()
      }
    } catch (error) {
      setStatus('error')
      logError(error)
      onUploadError()
    }
  })

  const handleDeletePress = useEvent(() => {
    onDeletePress()
  })

  useEffect(() => {
    if (shouldUpload && !hasUploadStarted.current) {
      hasUploadStarted.current = true
      void uploadWithStateUpdates()
    }
  }, [shouldUpload, uploadWithStateUpdates])

  return (
    <View flexDirection="row" items="center" flex={1} gap="$3">
      <Paragraph flex={1} textWrap="wrap">
        {fileName}
      </Paragraph>
      <View>
        <DocumentToUploadIcon
          status={status}
          disabled={!!disabled}
          onDeletePress={handleDeletePress}
        />
      </View>
    </View>
  )
}
